import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { FileUp, Eye, CheckCircle2, AlertTriangle, MinusCircle, Loader2, Clock, Pencil, Settings2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { forestaOpsApi, colppyApi } from '../../../lib/api';
import { formatDate } from '../../../lib/utils';
import { MonthPeriodHeader } from '../../../components/common/MonthPeriodHeader';

// Estados considerados "finalizados" (cobros cerrados): validados, pagados (en tiempo o tarde) y morosos cerrados quedan fuera —
// "moroso" sigue siendo un cobro que requiere acción → pendiente.
const FINALIZADO_STATES = new Set(['validado', 'pagado', 'pagado_tarde']);
const FILTROS = [
  { value: 'pendientes', label: 'Pendientes', match: (s) => !FINALIZADO_STATES.has(s) },
  { value: 'finalizados', label: 'Finalizados', match: (s) => FINALIZADO_STATES.has(s) },
];

const ESTADO_BADGE = {
  no_aplica: { label: 'No aplica', cls: 'bg-slate-100 text-slate-500 ring-slate-200', Icon: MinusCircle },
  pendiente: { label: 'Pendiente', cls: 'bg-orange-50 text-orange-700 ring-orange-100', Icon: Clock },
  en_revision: { label: 'En revisión', cls: 'bg-blue-50 text-blue-700 ring-blue-100', Icon: AlertTriangle },
  moroso: { label: 'Moroso', cls: 'bg-red-50 text-red-700 ring-red-100', Icon: AlertTriangle },
  observado: { label: 'Observado', cls: 'bg-amber-50 text-amber-700 ring-amber-100', Icon: AlertTriangle },
  validado: { label: 'Validado', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', Icon: CheckCircle2 },
  pagado_tarde: { label: 'Pagado tarde', cls: 'bg-amber-50 text-amber-800 ring-amber-100', Icon: AlertTriangle },
  parcial: { label: 'Parcial', cls: 'bg-yellow-50 text-yellow-800 ring-yellow-100', Icon: AlertTriangle },
};

const fmtMoney = (n) => n == null ? '—' : new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const PagoDialog = ({ open, onOpenChange, unidad, edificioId, periodo, onSaved }) => {
  const [fechaPago, setFechaPago] = useState('');
  const [montoPagado, setMontoPagado] = useState('');
  const [numeroOp, setNumeroOp] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && unidad) {
      setFechaPago(unidad.comprobante?.fecha_pago || '');
      setMontoPagado(unidad.comprobante?.monto_pagado != null ? String(unidad.comprobante.monto_pagado) : '');
      setNumeroOp(unidad.comprobante?.numero_operacion || '');
      setObservaciones(unidad.comprobante?.observaciones || '');
    }
  }, [open, unidad]);

  const handleSave = async () => {
    if (!unidad) return;
    setSaving(true);
    try {
      const data = {
        fecha_pago: fechaPago || null,
        monto: montoPagado === '' ? null : Number(montoPagado),
        numero_operacion: numeroOp || null,
        observaciones: observaciones || null,
        metodo_carga_fecha: 'manual',
      };
      await forestaOpsApi.patchComprobante(edificioId, unidad.unidad_id, periodo, data);
      toast.success('Datos de pago guardados');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Cargar pago manual — {unidad?.unidad_nombre}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Fecha de pago</Label>
            <Input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} data-testid="cobro-fecha-pago" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Monto pagado (ARS)</Label>
            <Input type="number" min="0" step="0.01" value={montoPagado} onChange={(e) => setMontoPagado(e.target.value)} placeholder={unidad?.monto_alquiler != null ? `Alquiler: ${unidad.monto_alquiler}` : 'Monto pagado'} data-testid="cobro-monto-pagado" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">N° operación / transferencia</Label>
            <Input value={numeroOp} onChange={(e) => setNumeroOp(e.target.value)} placeholder="Opcional" data-testid="cobro-numero-op" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Observaciones</Label>
            <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Opcional" data-testid="cobro-obs" />
          </div>
          <div className="text-[11px] text-slate-500 bg-slate-50 px-3 py-2 rounded">
            Método: <b>manual</b>. En la próxima fase la fecha y el monto se podrán leer automáticamente del comprobante.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="cobro-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin mr-1.5" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TabCobrosForesta = ({ edificioId, periodo, setSelectedPeriodo, activeTab }) => {
  const [data, setData] = useState({ items: [], totales: {}, config: {}, contadores: {} });
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [filtro, setFiltro] = useState('pendientes');
  const [pagoDialog, setPagoDialog] = useState(null);
  const [syncingColppy, setSyncingColppy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await forestaOpsApi.listCobros(edificioId, periodo);
      setData(r.data);
    } catch (e) { toast.error('Error al cargar cobros'); }
    finally { setLoading(false); }
  }, [edificioId, periodo]);

  useEffect(() => {
    if (activeTab !== 'cobros') return;
    load();
  }, [activeTab, load]);

  const itemsFiltrados = useMemo(() => {
    const f = FILTROS.find(x => x.value === filtro);
    if (!f?.match) return data.items;
    return data.items.filter(it => f.match(it.estado_cobro));
  }, [data.items, filtro]);

  const uploadComprobante = async (unidadId, file) => {
    setUploadingKey(`u-${unidadId}`);
    try {
      await forestaOpsApi.uploadComprobante(edificioId, unidadId, periodo, file);
      toast.success('Comprobante subido');
      load();
    } catch (e) { toast.error('Error al subir comprobante'); }
    finally { setUploadingKey(null); }
  };

  const setEstado = async (unidadId, estado, observaciones) => {
    try {
      await forestaOpsApi.patchComprobante(edificioId, unidadId, periodo, { estado, ...(observaciones != null ? { observaciones } : {}) });
      toast.success(`Comprobante: ${estado}`);
      load();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  // Sincronización con Colppy: trae cobros del ERP y los matchea con los items del período.
  // Reutiliza el endpoint legacy `/api/colppy/sincronizar-cobros/{edif}?periodo={periodo}`
  // que ya devolvía `{ ok, resumen: { actualizados, ya_cobrados, sin_cliente_colppy, errores } }`.
  // Tras sincronizar, refresca la tabla con load().
  const handleSyncColppy = async () => {
    if (!edificioId || !periodo) return;
    setSyncingColppy(true);
    try {
      const res = await colppyApi.sincronizarCobros(edificioId, periodo);
      const data = res?.data || {};
      if (data.ok) {
        const { actualizados = 0, ya_cobrados = 0, sin_cliente_colppy = 0, errores = 0 } = data.resumen || {};
        if (actualizados > 0) {
          toast.success(`Sincronización completada: ${actualizados} cobro(s) actualizado(s)`);
        } else if (ya_cobrados > 0) {
          toast.info('Todos los cobros ya estaban sincronizados');
        } else if (sin_cliente_colppy > 0) {
          toast.warning(`${sin_cliente_colppy} unidad(es) sin cliente en Colppy`);
        } else {
          toast.info('No se encontraron cobros para actualizar');
        }
        if (errores > 0) toast.error(`${errores} error(es) durante la sincronización`);
        await load();
      } else {
        toast.error('Error al sincronizar con Colppy');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error de conexión con Colppy');
    } finally {
      setSyncingColppy(false);
    }
  };

  const cfg = data.config || {};
  const totales = data.totales || {};
  // Recalcula los contadores en cliente para que coincidan con la nueva semántica
  // de "Pendientes" (todo lo no finalizado) y "Finalizados" (validado/pagado/pagado_tarde).
  const contadores = useMemo(() => {
    const items = data.items || [];
    const out = {};
    FILTROS.forEach(f => {
      out[f.value] = f.match ? items.filter(it => f.match(it.estado_cobro)).length : items.length;
    });
    return out;
  }, [data.items]);

  // KPIs: Total (cobros esperados con contrato), Pendientes (no finalizados),
  // Vencidos (pendientes con días de atraso > 0).
  const cobroKpis = useMemo(() => {
    const items = (data.items || []).filter(it => it.tiene_contrato);
    const pendientesArr = items.filter(it => !FINALIZADO_STATES.has(it.estado_cobro));
    const cobradasArr = items.filter(it => FINALIZADO_STATES.has(it.estado_cobro));
    const vencidos = pendientesArr.filter(it => (it.dias_atraso || 0) > 0).length;
    const montoCobrado = cobradasArr.reduce((s, it) => s + (it.comprobante?.monto_pagado ?? it.total_adeudado ?? it.monto_alquiler ?? 0), 0);
    const montoPendiente = pendientesArr.reduce((s, it) => s + (it.total_adeudado || it.monto_alquiler || 0), 0);
    return { total: items.length, pendientes: pendientesArr.length, vencidos, montoCobrado, montoPendiente };
  }, [data.items]);

  return (
    <TabsContent value="cobros" className="mt-0">
      <div className="space-y-3 flex-1 min-h-0 flex flex-col">
        {setSelectedPeriodo && (
          <MonthPeriodHeader
            value={periodo}
            onChange={setSelectedPeriodo}
            testid="foresta-header-cobros"
            right={
              <>
                <div className="flex items-center gap-3 flex-nowrap text-sm shrink-0 mr-1" data-testid="cobro-kpis">
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="text-slate-500">Total:</span>
                    <span className="font-bold text-slate-900" data-testid="cobro-kpi-total">{cobroKpis.total}</span>
                  </div>
                  <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="text-slate-500">Pendientes:</span>
                    <span className={`font-bold ${cobroKpis.pendientes > 0 ? 'text-amber-600' : 'text-slate-900'}`} data-testid="cobro-kpi-pendientes">{cobroKpis.pendientes}</span>
                  </div>
                  <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="text-slate-500">Vencidos:</span>
                    <span className={`font-bold ${cobroKpis.vencidos > 0 ? 'text-red-600' : 'text-slate-900'}`} data-testid="cobro-kpi-vencidos">{cobroKpis.vencidos}</span>
                  </div>
                  <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="text-slate-500">Cobrado:</span>
                    <span className="font-bold text-green-700" data-testid="cobro-kpi-cobrado">{fmtMoney(cobroKpis.montoCobrado)}</span>
                  </div>
                  <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="text-slate-500">Pendiente:</span>
                    <span className="font-bold text-amber-600" data-testid="cobro-kpi-monto-pendiente">{fmtMoney(cobroKpis.montoPendiente)}</span>
                  </div>
                </div>
                <span className="text-xs text-slate-500 ml-1">Filtrar:</span>
                {FILTROS.map(f => (
                  <Button
                    key={f.value}
                    variant={filtro === f.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFiltro(f.value)}
                    className="text-xs h-8"
                    data-testid={`cobro-filter-${f.value}`}
                  >
                    {f.label}
                    {contadores[f.value] != null && (
                      <span className="ml-1 opacity-70">({contadores[f.value]})</span>
                    )}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncColppy}
                  disabled={syncingColppy}
                  className="text-xs h-8 gap-1.5 ml-1"
                  data-testid="cobros-foresta-sync-colppy-btn"
                  title="Sincronizar cobros con Colppy"
                >
                  {syncingColppy
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />
                  }
                  {syncingColppy ? 'Sincronizando...' : 'Sincronizar Colppy'}
                </Button>
              </>
            }
          />
        )}

        <div className="bg-white rounded-lg border border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : itemsFiltrados.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No hay registros para este filtro.</div>
          ) : (
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm min-h-full">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Unidad</th>
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Propietario</th>
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Inquilino</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Alquiler</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Venc.</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Fecha pago</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Monto pagado</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Comprob.</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Días</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Intereses</th>
                    <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Total</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Estado</th>
                    <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsFiltrados.map(it => {
                    const noContrato = !it.tiene_contrato;
                    const meta = ESTADO_BADGE[it.estado_cobro] || ESTADO_BADGE.pendiente;
                    const tieneArchivo = !!it.comprobante?.archivo_id;
                    return (
                      <tr
                        key={it.unidad_id}
                        className={`border-b border-slate-100 hover:bg-slate-50 ${noContrato ? 'opacity-50 bg-slate-50/30' : ''}`}
                        data-testid={`cobro-row-${it.unidad_id}`}
                      >
                        <td className="py-2.5 px-3 font-medium text-slate-900 whitespace-nowrap">
                          {it.unidad_nombre}
                          <div className="text-[10px] text-slate-400 capitalize">{it.tipo}</div>
                        </td>
                        <td className="py-2.5 px-3 text-slate-700 max-w-[180px] truncate" title={it.propietario_nombre}>{it.propietario_nombre || '—'}</td>
                        <td className="py-2.5 px-3 text-slate-700 max-w-[160px] truncate" title={it.inquilino || ''}>{it.inquilino || '—'}</td>
                        <td className="py-2.5 px-2 text-right text-slate-700 whitespace-nowrap">{fmtMoney(it.monto_alquiler)}</td>
                        <td className="py-2.5 px-2 text-center text-[11px] text-slate-600 whitespace-nowrap">{it.vencimiento ? formatDate(it.vencimiento) : '—'}</td>
                        <td className="py-2.5 px-2 text-center text-[11px] text-slate-700 whitespace-nowrap" data-testid={`cobro-fpago-${it.unidad_id}`}>
                          {it.comprobante?.fecha_pago ? formatDate(it.comprobante.fecha_pago) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-2.5 px-2 text-right text-slate-700 whitespace-nowrap">{it.comprobante?.monto_pagado != null ? fmtMoney(it.comprobante.monto_pagado) : '—'}</td>
                        {/* Comprobante */}
                        <td className="py-2.5 px-2 text-center" data-testid={`cobro-comp-${it.unidad_id}`}>
                          {noContrato ? (
                            <span className="text-[11px] text-slate-400">N/A</span>
                          ) : (
                            <div className="flex items-center justify-center gap-0.5">
                              {tieneArchivo && (
                                <a href={forestaOpsApi.archivoUrl(it.comprobante.archivo_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="Ver comprobante" data-testid={`cobro-ver-${it.unidad_id}`}>
                                  <Eye className="w-3.5 h-3.5" />
                                </a>
                              )}
                              <TooltipProvider delayDuration={200}><Tooltip>
                                <TooltipTrigger asChild>
                                  <label className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100 cursor-pointer" data-testid={`cobro-upload-${it.unidad_id}`}>
                                    {uploadingKey === `u-${it.unidad_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadComprobante(it.unidad_id, f); e.target.value=''; }} />
                                  </label>
                                </TooltipTrigger>
                                <TooltipContent>{tieneArchivo ? 'Reemplazar comprobante' : 'Subir comprobante'}</TooltipContent>
                              </Tooltip></TooltipProvider>
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${it.dias_atraso > 0 ? 'text-red-700 bg-red-50' : 'text-slate-500'}`}>
                            {it.dias_atraso > 0 ? <AlertTriangle className="w-3 h-3" /> : null}
                            {it.dias_atraso}d
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right text-red-600 whitespace-nowrap">{it.intereses > 0 ? fmtMoney(it.intereses) : '—'}</td>
                        <td className="py-2.5 px-2 text-right font-semibold text-slate-900 whitespace-nowrap">{it.total_adeudado > 0 ? fmtMoney(it.total_adeudado) : '—'}</td>
                        <td className="py-2.5 px-2 text-center" data-testid={`cobro-estado-${it.unidad_id}`}>
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ${meta.cls}`}>
                            <meta.Icon className="w-3 h-3" />
                            {meta.label}
                          </span>
                        </td>
                        {/* Acciones */}
                        <td className="py-2.5 px-2 text-center">
                          {noContrato ? (
                            <span className="text-[11px] text-slate-400">—</span>
                          ) : (
                            <div className="flex items-center justify-center gap-0.5">
                              <TooltipProvider delayDuration={200}><Tooltip>
                                <TooltipTrigger asChild>
                                  <button onClick={() => setPagoDialog(it)} className="inline-flex items-center justify-center w-6 h-6 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50" data-testid={`cobro-edit-${it.unidad_id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Cargar/editar pago</TooltipContent>
                              </Tooltip></TooltipProvider>
                              {tieneArchivo && it.comprobante?.estado !== 'validado' && (
                                <TooltipProvider delayDuration={200}><Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => setEstado(it.unidad_id, 'validado')} className="inline-flex items-center justify-center w-6 h-6 rounded text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50" data-testid={`cobro-validar-${it.unidad_id}`}>
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>Validar comprobante</TooltipContent>
                                </Tooltip></TooltipProvider>
                              )}
                              {tieneArchivo && it.comprobante?.estado !== 'observado' && (
                                <TooltipProvider delayDuration={200}><Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => {
                                      const obs = window.prompt('Observación del comprobante:', it.comprobante?.observaciones || '');
                                      if (obs != null) setEstado(it.unidad_id, 'observado', obs);
                                    }} className="inline-flex items-center justify-center w-6 h-6 rounded text-amber-600 hover:text-amber-800 hover:bg-amber-50" data-testid={`cobro-observar-${it.unidad_id}`}>
                                      <AlertTriangle className="w-3.5 h-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>Observar comprobante</TooltipContent>
                                </Tooltip></TooltipProvider>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <PagoDialog
        open={!!pagoDialog}
        onOpenChange={(o) => !o && setPagoDialog(null)}
        unidad={pagoDialog}
        edificioId={edificioId}
        periodo={periodo}
        onSaved={load}
      />
    </TabsContent>
  );
};

export default TabCobrosForesta;
